import { useEffect } from 'react'
import { Button, Group, SegmentedControl, Slider, Text } from '@mantine/core'
import { formatClock, formatDuration } from '@racedash/shared'
import { replaySpeeds, type ReplaySpeed, type useReplayClock } from './replay'

/**
 * The replay's controls: play and pause, how fast, and where — a scrub bar
 * over the session from the green flag to the flag, and steps either way for
 * the moment just missed.
 */
export default function ReplayBar({
  clock,
  loading,
  error,
  onExit,
}: {
  clock: ReturnType<typeof useReplayClock>
  loading: boolean
  error: string | null
  onExit: () => void
}) {
  const { at, playing, setPlaying, speed, setSpeed, seek, start, end } = clock
  const ready = at != null && start != null && end != null

  // Space plays and pauses, as in any player — but not while typing, or on a
  // control that space already presses.
  useEffect(() => {
    if (!ready) return
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      if (e.key !== ' ' || el?.closest('input, textarea, select, button, [role="slider"], [contenteditable="true"]')) return
      e.preventDefault()
      setPlaying((p) => !p)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [ready, setPlaying])

  const step = (s: number) => at && seek(at.getTime() + s * 1000)

  return (
    <div className="replay-bar">
      <Group gap="xs" wrap="wrap" align="center">
        <Text fw={600} size="sm" c="grape">
          Replay
        </Text>
        {!ready ? (
          <Text size="sm" c={error ? 'red' : 'dimmed'}>
            {error ? `Could not load the session: ${error}` : loading ? 'Loading the session…' : 'Nothing to replay yet.'}
          </Text>
        ) : (
          <>
            <Button size="xs" w={78} onClick={() => setPlaying(!playing)} disabled={!playing && at.getTime() >= end.getTime()}>
              {playing ? '❚❚ Pause' : '▶ Play'}
            </Button>
            <SegmentedControl
              size="xs"
              aria-label="Replay speed"
              data={replaySpeeds.map((s) => ({ value: String(s), label: `${s}×` }))}
              value={String(speed)}
              onChange={(v) => setSpeed(Number(v) as ReplaySpeed)}
            />
            <Group gap={4} wrap="nowrap">
              <Button size="xs" variant="default" onClick={() => step(-300)}>
                −5m
              </Button>
              <Button size="xs" variant="default" onClick={() => step(-60)}>
                −1m
              </Button>
              <Button size="xs" variant="default" onClick={() => step(60)}>
                +1m
              </Button>
              <Button size="xs" variant="default" onClick={() => step(300)}>
                +5m
              </Button>
            </Group>
            <Text size="sm" ff="monospace" className="replay-time">
              {formatClock(at, { seconds: true })}
              <Text span size="xs" c="dimmed">
                {' '}
                +{formatDuration(at.getTime() - start.getTime())}
              </Text>
            </Text>
          </>
        )}
        <Button size="xs" variant="subtle" ml="auto" onClick={onExit}>
          Back to live
        </Button>
      </Group>
      {ready && (
        <Slider
          className="replay-scrub"
          aria-label="Replay position"
          min={0}
          max={Math.max(1, Math.round((end.getTime() - start.getTime()) / 1000))}
          step={1}
          value={Math.round((at.getTime() - start.getTime()) / 1000)}
          onChange={(s) => seek(start.getTime() + s * 1000)}
          label={(s) => formatClock(new Date(start.getTime() + s * 1000), { seconds: true })}
          size="sm"
          color="grape"
        />
      )}
    </div>
  )
}
